import React, { useEffect, useMemo, useState } from 'react';
import { useZunelo } from '../../context/ZuneloContext';
import { SLACK_AXES, sortRecentAttentionItems } from '../../domain/filtering';
import { defaultAxisFilterState, filterByAxes } from '../../domain/multiAxisFiltering';
import { FilterButton } from '../../ui/common/FilterButton';
import { PopoverButton } from '../../ui/common/PopoverButton';
import { SlackCard } from './SlackCard';
import { Button, useProviderRefresh } from '../../ui/common';
import { groupSlackItems } from '../../domain/slackGrouping';
import { shouldUseMockSlack } from '../../services/clients';
import { UnattendedInboxPanel } from './UnattendedInboxPanel';
import { slackClient } from '../../services/clients';
import { SourceFeedState } from '../sources/SourceFeedState';
import { SourceGate } from '../sources/SourceGate';

const GROUPING_OPTIONS = [
  { value: 'flat', label: 'Plano' },
  { value: 'channel', label: 'Por canal' },
  { value: 'task', label: 'Por tarea' },
] as const;

export const SlackView: React.FC = () => {
  const {
    items,
    activeAxisFilters,
    setActiveAxisFilters,
    slackGrouping,
    setSlackGrouping,
    mergeSet,
    setIsMergeModalOpen,
    clearMergeSet,
    selectedId,
    loadingSources,
    errors,
    retry,
    refreshUnattendedInbox,
  } = useZunelo();
  const [viewOpen, setViewOpen] = useState(false);
  const {
    isRefreshing: syncing,
    error: syncError,
    refresh: syncSlack,
  } = useProviderRefresh('slack', retry, () => slackClient.refresh(24).then(() => undefined));

  const slackItems = useMemo(
    () => sortRecentAttentionItems(items.filter((i) => i.source === 'slack')),
    [items],
  );
  const currentFilters = activeAxisFilters['slack'] || defaultAxisFilterState(SLACK_AXES);
  const isLoading = loadingSources.slack && slackItems.length === 0;
  const error = errors.slack;
  const liveSlack = !shouldUseMockSlack();
  const authHint =
    liveSlack &&
    Boolean(error) &&
    /unauthorized|email_unverified|401|firebase|sign.?in/i.test(error ?? '');
  const [surface, setSurface] = useState<'threads' | 'unattended'>('threads');

  useEffect(() => {
    if (surface === 'unattended') void refreshUnattendedInbox();
  }, [surface, refreshUnattendedInbox]);

  const filteredItems = useMemo(
    () => filterByAxes(slackItems, SLACK_AXES, currentFilters),
    [slackItems, currentFilters],
  );
  const groupedItems = useMemo(
    () => groupSlackItems(filteredItems, slackGrouping),
    [filteredItems, slackGrouping],
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <SourceGate
        source="slack"
        error={error}
        isLoading={isLoading}
        totalCount={slackItems.length}
        isRetrying={loadingSources.slack}
        onRetry={() => retry('slack')}
      >
        <div
          role="tablist"
          aria-label="Superficie de Slack"
          style={{
            display: 'flex',
            alignSelf: 'flex-start',
            gap: 3,
            padding: 3,
            borderRadius: 10,
            background: 'var(--z-fill-2)',
          }}
        >
          {(['threads', 'unattended'] as const).map((value) => {
            const active = surface === value;
            return (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setSurface(value)}
                style={{
                  padding: '7px 12px',
                  border: 'none',
                  borderRadius: 8,
                  background: active ? 'var(--z-surface)' : 'transparent',
                  color: active ? 'var(--z-text)' : 'var(--z-muted)',
                  boxShadow: active ? '0 1px 3px var(--z-sh-4)' : 'none',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                {value === 'threads' ? 'Hilos' : 'Asuntos sin atender'}
              </button>
            );
          })}
        </div>
        {surface === 'unattended' ? (
          <UnattendedInboxPanel onOpenThreads={() => setSurface('threads')} />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}
            >
              {syncError ? (
                <span role="alert" style={{ color: 'var(--z-danger)', fontSize: 12 }}>
                  {syncError}
                </span>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void syncSlack()}
                disabled={syncing || loadingSources.slack}
              >
                {syncing ? 'Sincronizando…' : 'Sincronizar'}
              </Button>
            </div>
            {/* Filtros & Vista (grouping) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <FilterButton
                axes={SLACK_AXES}
                items={slackItems}
                value={currentFilters}
                onChange={(next) => setActiveAxisFilters('slack', next)}
              />

              <PopoverButton label="Vista" open={viewOpen} onOpenChange={setViewOpen}>
                <div
                  style={{
                    padding: 12,
                    minWidth: 160,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ fontSize: 12, color: 'var(--z-muted)', marginBottom: 4 }}>
                    Agrupación
                  </div>
                  {GROUPING_OPTIONS.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={slackGrouping === value}
                      onClick={() => {
                        setSlackGrouping(value);
                        setViewOpen(false);
                      }}
                      style={{
                        textAlign: 'left',
                        padding: '6px 10px',
                        borderRadius: 8,
                        fontSize: 12.5,
                        fontWeight: 500,
                        cursor: 'pointer',
                        border: 'none',
                        background: slackGrouping === value ? 'var(--z-accent)' : 'transparent',
                        color: slackGrouping === value ? 'var(--z-on-accent)' : 'var(--z-muted)',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </PopoverButton>
            </div>

            {/* Floating Merge Selection Bar */}
            {mergeSet.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 16px',
                  borderRadius: 12,
                  background: 'var(--z-accent-soft)',
                  border: 'none',
                  color: 'var(--z-accent-copy)',
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {mergeSet.length}{' '}
                  {mergeSet.length === 1 ? 'hilo seleccionado' : 'hilos seleccionados'} para
                  comparar
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button variant="ghost" size="sm" onClick={clearMergeSet}>
                    Cancelar
                  </Button>
                  <Button variant="primary" size="sm" onClick={() => setIsMergeModalOpen(true)}>
                    Abrir vista combinada ({mergeSet.length})
                  </Button>
                </div>
              </div>
            )}

            <SourceFeedState
              source="slack"
              error={
                error && authHint
                  ? `${error} — cierra sesión y vuelve a entrar con una cuenta cuyo correo esté verificado.`
                  : error
              }
              isLoading={isLoading}
              isRetrying={loadingSources.slack}
              onRetry={() => retry('slack')}
              totalCount={slackItems.length}
              visibleCount={filteredItems.length}
              onShowAll={() => setActiveAxisFilters('slack', defaultAxisFilterState(SLACK_AXES))}
              listGap={18}
            >
              {groupedItems.map((group) => (
                <div key={group.key} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {group.label && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--z-muted-2)',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      <span>{group.label}</span>
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 10.5,
                          padding: '1px 6px',
                          borderRadius: 6,
                          background: 'var(--z-fill-2)',
                          color: 'var(--z-dim)',
                        }}
                      >
                        {group.items.length}
                      </span>
                    </div>
                  )}
                  {group.items.map((item) => (
                    <SlackCard
                      key={item.id}
                      item={item}
                      isSelected={selectedId === item.id}
                      isMergeSelected={mergeSet.includes(item.id)}
                    />
                  ))}
                </div>
              ))}
            </SourceFeedState>
          </div>
        )}
      </SourceGate>
    </div>
  );
};
